import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Package, Settings, MessageSquare, CreditCard, Bell, Truck, Users, Trash2, CheckCheck, Banknote } from "lucide-react";
import { fleetrifyIcons } from "../../assets/icons/icons";

// Swipe-to-delete works on both touch (mobile) and mouse drag (desktop);
// touchAction is only needed on mobile so vertical scroll isn't hijacked.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 640
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

interface Notification {
  id: string;
  avatarSrc?: string;
  avatarText?: string;
  sender: string;
  message: string;
  timestamp: string;
  isNew?: boolean;
  // Where tapping the notification goes, for types that point at something.
  href?: string;
}

interface NotificationItemProps {
  filteredNotifications: Notification[];
  isLoading: boolean;
  handleDismiss: (id: string) => void;
  handleMarkAsRead: (id: string) => void;
  handleOpen?: (notification: Notification) => void;
}

const getSenderMeta = (sender: string): { icon: React.ReactNode; bg: string; ring: string } => {
  const s = (sender || "").toLowerCase();
  // Checked before the generic "payment" bucket below, since a type like
  // "cash_payment_confirmed" would otherwise just match that instead.
  if (s.includes("cash")) return { icon: <Banknote size={14} />, bg: "bg-emerald-50", ring: "bg-emerald-500" };
  if (s.includes("order")) return { icon: <Package size={14} />, bg: "bg-blue-50", ring: "bg-blue-500" };
  if (s.includes("fleet") || s.includes("vehicle") || s.includes("driver")) return { icon: <Truck size={14} />, bg: "bg-indigo-50", ring: "bg-indigo-500" };
  if (s.includes("message") || s.includes("chat")) return { icon: <MessageSquare size={14} />, bg: "bg-green-50", ring: "bg-green-500" };
  if (s.includes("wallet") || s.includes("payment") || s.includes("finance")) return { icon: <CreditCard size={14} />, bg: "bg-amber-50", ring: "bg-amber-500" };
  if (s.includes("user") || s.includes("staff") || s.includes("member")) return { icon: <Users size={14} />, bg: "bg-purple-50", ring: "bg-purple-500" };
  if (s.includes("setting") || s.includes("system")) return { icon: <Settings size={14} />, bg: "bg-gray-100", ring: "bg-gray-400" };
  return { icon: <Bell size={14} />, bg: "bg-[#e8edf7]", ring: "bg-[#143887]" };
};

const renderSkeleton = (index: number) => (
  <div key={`skeleton-${index}`} className="flex items-start gap-3 px-5 py-4 border-b border-gray-100">
    <div className="w-9 h-9 rounded-xl bg-gray-100 animate-pulse flex-shrink-0" />
    <div className="flex-1 space-y-2 pt-0.5">
      <div className="h-3 bg-gray-100 rounded-full w-4/5 animate-pulse" />
      <div className="h-2.5 bg-gray-100 rounded-full w-3/5 animate-pulse" />
      <div className="h-2 bg-gray-100 rounded-full w-1/3 animate-pulse" />
    </div>
  </div>
);

const NotificationItem: React.FC<NotificationItemProps> = ({
  filteredNotifications,
  isLoading,
  handleDismiss,
  handleMarkAsRead,
  handleOpen,
}) => {
  const isMobile = useIsMobile();

  if (isLoading) {
    return <div>{Array.from({ length: 5 }).map((_, i) => renderSkeleton(i))}</div>;
  }

  if (filteredNotifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
        <div className="mb-2">{fleetrifyIcons.emptyState}</div>
        <p className="text-sm font-bold text-[#00032A] mb-1">All caught up!</p>
        <p className="text-xs text-gray-400 leading-relaxed max-w-[200px]">
          No notifications right now. We'll let you know when something needs your attention.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-4">
      <AnimatePresence initial={false}>
        {filteredNotifications.map((notification, index) => {
          const { id, sender, message, timestamp, isNew = false } = notification;
          const meta = getSenderMeta(sender);

          return (
            <motion.div
              key={id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: 60, transition: { duration: 0.2 } }}
              transition={{ duration: 0.22, delay: index * 0.04, ease: "easeOut" }}
              className="relative overflow-hidden border-b border-gray-50"
            >
              {/* Revealed underneath as the row is dragged: right swipe → delete (left side), left swipe → mark as read (right side) */}
              <div className="absolute inset-0 flex" aria-hidden="true">
                <div className="w-1/2 h-full flex items-center pl-6 bg-red-500">
                  <Trash2 size={16} className="text-white" />
                </div>
                <div className="w-1/2 h-full flex items-center justify-end pr-6 bg-blue-500">
                  <CheckCheck size={16} className="text-white" />
                </div>
              </div>

              <motion.div
                drag="x"
                dragDirectionLock
                dragConstraints={{ left: -88, right: 88 }}
                dragElastic={0.15}
                dragSnapToOrigin
                dragMomentum={false}
                style={isMobile ? { touchAction: "pan-y" } : undefined}
                onDragEnd={(_event, info) => {
                  if (info.offset.x < -70) handleMarkAsRead(id);
                  else if (info.offset.x > 70) handleDismiss(id);
                }}
                // onTap (not onClick) so finishing a swipe doesn't also open it.
                // Taps on the dismiss button (data-no-open) don't open it.
                onTap={notification.href && handleOpen ? (event) => {
                  if ((event.target as HTMLElement | null)?.closest?.("[data-no-open]")) return;
                  handleOpen(notification);
                } : undefined}
                role={notification.href ? "link" : undefined}
                tabIndex={notification.href ? 0 : undefined}
                onKeyDown={notification.href && handleOpen ? (e) => { if (e.key === "Enter") handleOpen(notification); } : undefined}
                className={`relative group flex items-start gap-3 px-5 py-4 transition-colors
                  ${isNew ? "bg-[#f5f8ff]" : "bg-white hover:bg-gray-50/80"} ${notification.href ? "cursor-pointer" : ""}`}
              >
                {isNew && (
                  <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-[#143887]" />
                )}

                <div className={`flex-shrink-0 w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center text-gray-600 mt-0.5`}>
                  {meta.icon}
                </div>

                <div className="flex-1 min-w-0 pr-6">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-snug ${isNew ? "font-semibold text-[#00032A]" : "font-medium text-gray-600"}`}>
                      {message}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${meta.bg} ${isNew ? "text-[#143887]" : "text-gray-500"}`}>
                      <span className={`w-1 h-1 rounded-full ${meta.ring}`} />
                      {sender}
                    </span>
                    <span className="text-[10px] text-gray-400">{timestamp}</span>
                    {!isNew && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">
                        <CheckCheck size={10} />
                        Read
                      </span>
                    )}
                  </div>
                </div>

                <motion.button
                  onClick={(e) => { e.stopPropagation(); handleDismiss(id); }}
                  data-no-open
                  className="absolute right-4 top-4 p-1 rounded-full text-gray-300 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-400 transition-all hidden sm:inline-flex"
                  whileTap={{ scale: 0.85 }}
                  aria-label="Dismiss notification"
                >
                  <X size={13} />
                </motion.button>
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default NotificationItem;
